import Paper from '@/components/Paper'
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip'

export type StatCardColor =
    | 'default'
    | 'success'
    | 'warning'
    | 'approaching'
    | 'danger'
    | 'info'

// Match DC StatsCards convention:
// White card body, gray-100 border, small colored icon-box only.
const ICON_BG: Record<StatCardColor, string> = {
    default: 'bg-slate-50',
    success: 'bg-green-50',
    warning: 'bg-amber-50',
    approaching: 'bg-orange-50',
    danger: 'bg-red-50',
    info: 'bg-blue-50',
}

const ICON_FG: Record<StatCardColor, string> = {
    default: 'text-slate-600',
    success: 'text-green-600',
    warning: 'text-amber-600',
    approaching: 'text-orange-600',
    danger: 'text-red-600',
    info: 'text-blue-600',
}

interface StatCardProps {
    title: string
    value: string | number
    subValue?: string
    icon: React.ElementType
    color?: StatCardColor
    /** Optional tooltip formula shown on hover of value */
    tooltip?: string
}

const StatCard = ({
    title,
    value,
    subValue,
    icon: Icon,
    color = 'default',
    tooltip,
}: StatCardProps) => {
    const valueNode = tooltip ? (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="underline decoration-dotted underline-offset-2 cursor-help text-xl font-semibold text-foreground">
                    {value}
                </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
                {tooltip}
            </TooltipContent>
        </Tooltip>
    ) : (
        <p className="text-xl font-semibold text-foreground">{value}</p>
    )

    return (
        <Paper className="border border-gray-100">
            <div className="flex items-center gap-3">
                <div
                    className={`w-10 h-10 rounded-lg ${ICON_BG[color]} ${ICON_FG[color]} flex items-center justify-center flex-shrink-0`}
                >
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground truncate">
                        {title}
                    </p>
                    {valueNode}
                    {subValue && (
                        <p className="text-xs text-muted-foreground">
                            {subValue}
                        </p>
                    )}
                </div>
            </div>
        </Paper>
    )
}

export default StatCard
